import { Entity, EntityItem, Service, CustomAttributeType } from "electrodb";
import type { DynamoDBClient } from "@aws-sdk/client-dynamodb";
import { DynamoDBDocumentClient } from "@aws-sdk/lib-dynamodb";
import { tenantIdAttribute, dateAttribute } from "../db-attributes";

export const REPORT_STATUSES = ["generating", "ready", "failed"] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

export const REPORT_CADENCES = ["daily", "weekly", "monthly"] as const;
export type ReportCadence = (typeof REPORT_CADENCES)[number];

/**
 * ElectroDB entity for a consolidated tenant report (the artefact store).
 *
 * Grain — one row = one consolidated tenant report for one report type and
 * report period (design §4.2). Per-asset detail lives inside the stored
 * payload, not on the row.
 *
 * Key design:
 * - PK:      TENANT#{tenant}
 * - SK:      REPORT#{type}#{periodStart}              (main: per-type history + range)
 * - GSI1 PK: TENANT#{tenant}
 * - GSI1 SK: {periodStart}#{type}                     (chrono: newest-first, cross-type)
 * - GSI2 PK: REPORT#{reportId}                        (by-id: single-item lookup)
 * - GSI2 SK: (empty)
 *
 * The two indexes deliberately order the same components differently. The main
 * SK is type-major (`type#periodStart`) so a single report type's history is a
 * contiguous `begins_with`/`between` range, and a report resolves by its
 * external identity (`type` + `periodStart`). GSI1 is date-major
 * (`periodStart#type`) so the listing UI gets correct chronological pagination
 * (`ScanIndexForward=false`) and a cross-type date range in one query — neither
 * of which the type-major main SK can do. `cadence`/`status` stay off the keys
 * as filter dimensions (the rows are tiny, so filter expressions are cheap).
 *
 * `periodStart` is the ISO calendar date of the period's first day and is part
 * of both sort keys, so it is stored as a string (ElectroDB composite
 * attributes must be primitives usable directly in DynamoDB key conditions).
 *
 * Attribute name vs wire name: the entity names the attribute `tenant`
 * (matching a3s-media's convention) while the ReportJob wire contract (§4.10)
 * says `tenantId`. The reports-service mapping layer is the one place that
 * bridges the two; callers above it only ever see `tenantId`.
 */
export const ReportEntity = new Entity({
  model: { service: "a3s-reports", entity: "Report", version: "1" },
  attributes: {
    tenant: tenantIdAttribute,
    type: { type: "string", required: true }, // plugin.type, e.g. "wastesense"
    // Human-readable report frequency, sourced from plugin.cadence. A listing
    // filter/badge dimension, deliberately NOT a key component — see the index
    // doc above. Distinct from the plugin's `schedule` (cron+timezone).
    cadence: { type: REPORT_CADENCES, required: true },
    periodStart: dateAttribute, // first day of the report period (inclusive)
    periodEnd: dateAttribute, // last day of the report period (inclusive)
    reportId: { type: "string", required: true }, // ULID, stable across regen
    status: { type: REPORT_STATUSES, required: true },
    payloadS3Key: { type: "string" }, // <prefix>/payload.json — the generated output
    payloadSize: { type: "number" },
    jobS3Key: { type: "string" }, // <prefix>/job.json — the exact ReportJob inputs (for regen)
    // Generator output-contract version; a3s reads this to pick the renderer.
    payloadSchemaVersion: { type: "number", required: true },
    customerName: { type: "string", required: true }, // denormalised for listing
    // Opaque, plugin-defined blob (plugin.summariseForListing output). Typed
    // `any` rather than `map`+properties because the shape is owned by each
    // report-type plugin, not the engine — a `map` with declared properties
    // would silently strip every undeclared key on write (and an empty
    // `properties: {}` strips *all* keys). `any` stores the JSON verbatim.
    listingSummary: { type: CustomAttributeType<Record<string, unknown>>("any") },
    warnings: { type: "set", items: "string" }, // e.g. ["snapshots_partial"]
    generatedAt: { type: "string" },
    attempts: { type: "number", required: true },
    lastError: { type: "string" },
  },
  indexes: {
    byTenant: {
      pk: { field: "pk", composite: ["tenant"] }, // PK=TENANT#tid
      sk: { field: "sk", composite: ["type", "periodStart"] }, // SK=REPORT#type#periodStart
    },
    byTenantChrono: {
      index: "gsi1", // chronological listing: newest-first + cross-type date range
      pk: { field: "gsi1pk", composite: ["tenant"] }, // GSI1PK=TENANT#tid
      sk: { field: "gsi1sk", composite: ["periodStart", "type"] }, // GSI1SK=periodStart#type
    },
    // GSI2 — single-item lookup by the stable `reportId`. The client-API by-id
    // route (and the per-site artifact-presign route) resolve a report from a
    // bare `reportId` in the URL with no tenant/type/periodStart, so neither the
    // main index nor GSI1 (both tenant-PK'd) can serve it. `reportId` is a ULID,
    // unique per report across regen, so the PK alone identifies one row; the SK
    // is empty (no in-partition range needed). Tenant ISOLATION is NOT provided
    // by this index — the handler MUST compare the resolved row's `tenant`
    // against the caller's tenant (403 on mismatch), since anyone can put any
    // reportId in the URL.
    byReportId: {
      index: "gsi2",
      pk: { field: "gsi2pk", composite: ["reportId"] }, // GSI2PK=REPORT#reportId
      sk: { field: "gsi2sk", composite: [] }, // GSI2SK constant — single-item lookup
    },
  },
});

export type Report = EntityItem<typeof ReportEntity>;

export type ReportsDb = Service<{ Report: typeof ReportEntity }>;

/** Create a ReportsDb instance with the given table and DynamoDB client. */
export function makeReportsDb(deps: { table: string; client: DynamoDBClient }): ReportsDb {
  return new Service(
    { Report: ReportEntity },
    {
      table: deps.table,
      client: DynamoDBDocumentClient.from(deps.client),
    },
  );
}
